import config from '../config';
import { determineMaestroEventLocation, determineHHPModelEventLocation } from '../helpers/rules';
import { initSync } from './sync';
import WebsocketHelper from '../helpers/websocketHelper';

// LOCAL MOCK ONLY — do not merge. Streams synthetic ARGOS heart rate data
// for testing HeartRate chart autoscaling/axis behavior.
const MOCK_ARGOS_HR = false;
const MOCK_TICK_MS = 2000;
const MOCK_SEED_POINTS = 30;

function concatenateData(data, moreData, toBeContinued) {
  const concatenatedData = data.concat(moreData).sort((a, b) => {
    if (a.formattedTimestamp < b.formattedTimestamp) {
      return -1;
    }
    return 1;
  });
  if (!toBeContinued) {
    const mergedData = [];
    const currentMergedDataPoint = {};
    let currentTimestamp;
    concatenatedData.forEach((currentDataPoint) => {
      const { formattedTimestamp } = currentDataPoint || {};
      if (currentTimestamp && formattedTimestamp !== currentTimestamp) {
        mergedData.push({ ...currentMergedDataPoint });
      }
      currentTimestamp = formattedTimestamp;

      // overwrite currentMergedDataPoint with updated data for each key
      Object.keys(currentDataPoint || {}).forEach((key) => {
        currentMergedDataPoint[key] = currentDataPoint[key];
      });
    });
    return mergedData;
  }
  return concatenatedData;
}

export default class WebSocketService {
  constructor() {
    this.state = {
      connected: false,
      nblData: [],
      nblMinBtuHR: null,
      nblMaxBtuHR: null,
      nblMinHrBPM: null,
      nblMaxHrBPM: null,
      nblSuitPressure: null,
      nblPiCo2TimeByArea: {},
      nblMaestroEventData: [],
      nblECSData: [],
      chapeaData: [],
      chapeaMinBtuHR: null,
      chapeaMaxBtuHR: null,
      chapeaMinHrBPM: null,
      chapeaMaxHrBPM: null,
      chapeaSuitPressure: null,
      chapeaPiCo2TimeByArea: {},
      chapeaMaestroEventData: [],
      hmdEventData: [],
      argosData: [],
      argosMinBtuHR: null,
      argosMaxBtuHR: null,
      argosMinHrBPM: null,
      argosMaxHrBPM: null,
      argosSuitPressure: null,
      argosMaestroEventData: [],
      argosPiCo2TimeByArea: {},
      fieldTestData: [],
      fieldTestMinBtuHR: null,
      fieldTestMaxBtuHR: null,
      fieldTestMinHrBPM: null,
      fieldTestMaxHrBPM: null,
      fieldTestSuitPressure: null,
      fieldTestPiCo2TimeByArea: {},
      fieldTestMaestroEventData: [],
    };
    this.setState = () => {};
    this.resetState = (date, continueQueries) => {
      initSync(date, continueQueries).then((state) => {
        const {
          nblData: moreNblData, chapeaData: moreChapeaData,
          argosData: moreArgosData, fieldTestData: moreFieldTestData,
          nblMinBtuHR: newNblMinBtuHR, nblMaxBtuHR: newNblMaxBtuHR,
          nblMinHrBPM: newNblMinHrBPM, nblMaxHrBPM: newNblMaxHrBPM,
          chapeaMinBtuHR: newChapeaMinBtuHR, chapeaMaxBtuHR: newChapeaMaxBtuHR,
          chapeaMinHrBPM: newChapeaMinHrBPM, chapeaMaxHrBPM: newChapeaMaxHrBPM,
          argosMinBtuHR: newArgosMinBtuHR, argosMaxBtuHR: newArgosMaxBtuHR,
          argosMinHrBPM: newArgosMinHrBPM, argosMaxHrBPM: newArgosMaxHrBPM,
          fieldTestMinBtuHR: newFieldTestMinBtuHR, fieldTestMaxBtuHR: newFieldTestMaxBtuHR,
          fieldTestMinHrBPM: newFieldTestMinHrBPM, fieldTestMaxHrBPM: newFieldTestMaxHrBPM,
          nblPiCo2TimeByArea: moreNblPiCo2TimeByArea = {},
          chapeaPiCo2TimeByArea: moreChapeaPiCo2TimeByArea = {},
          argosPiCo2TimeByArea: moreArgosPiCo2TimeByArea = {},
          fieldTestPiCo2TimeByArea: moreFieldTestPiCo2TimeByArea = {},
          continueQueries: newContinueQueries,
        } = state || {};

        const {
          nblData, chapeaData, argosData, fieldTestData,
          nblMinBtuHR, nblMaxBtuHR, nblMinHrBPM, nblMaxHrBPM,
          chapeaMinBtuHR, chapeaMaxBtuHR, chapeaMinHrBPM, chapeaMaxHrBPM,
          argosMinBtuHR, argosMaxBtuHR, argosMinHrBPM, argosMaxHrBPM,
          fieldTestMinBtuHR, fieldTestMaxBtuHR, fieldTestMinHrBPM, fieldTestMaxHrBPM,
        } = this.state || {};
        let {
          nblPiCo2TimeByArea = {}, chapeaPiCo2TimeByArea = {},
          argosPiCo2TimeByArea = {}, fieldTestPiCo2TimeByArea = {},
        } = this.state || {};

        if (!nblPiCo2TimeByArea) {
          nblPiCo2TimeByArea = moreNblPiCo2TimeByArea;
        } else {
          Object.keys(moreNblPiCo2TimeByArea).forEach((ev) => {
            Object.keys(moreNblPiCo2TimeByArea[ev]).forEach((area) => {
              if (!nblPiCo2TimeByArea[ev]) {
                nblPiCo2TimeByArea[ev] = {};
              }
              if (!nblPiCo2TimeByArea[ev][area]) {
                nblPiCo2TimeByArea[ev][area] = 0;
              }
              nblPiCo2TimeByArea[ev][area] += moreNblPiCo2TimeByArea[ev][area];
            });
          });
        }
        if (!chapeaPiCo2TimeByArea) {
          chapeaPiCo2TimeByArea = moreChapeaPiCo2TimeByArea;
        } else {
          Object.keys(moreChapeaPiCo2TimeByArea).forEach((ev) => {
            Object.keys(moreChapeaPiCo2TimeByArea[ev]).forEach((area) => {
              if (!chapeaPiCo2TimeByArea[ev]) {
                chapeaPiCo2TimeByArea[ev] = {};
              }
              if (!chapeaPiCo2TimeByArea[ev][area]) {
                chapeaPiCo2TimeByArea[ev][area] = 0;
              }
              chapeaPiCo2TimeByArea[ev][area] += moreChapeaPiCo2TimeByArea[ev][area];
            });
          });
        }
        if (!argosPiCo2TimeByArea) {
          argosPiCo2TimeByArea = moreArgosPiCo2TimeByArea;
        } else {
          Object.keys(moreArgosPiCo2TimeByArea).forEach((ev) => {
            Object.keys(moreArgosPiCo2TimeByArea[ev]).forEach((area) => {
              if (!argosPiCo2TimeByArea[ev]) {
                argosPiCo2TimeByArea[ev] = {};
              }
              if (!argosPiCo2TimeByArea[ev][area]) {
                argosPiCo2TimeByArea[ev][area] = 0;
              }
              argosPiCo2TimeByArea[ev][area] += moreArgosPiCo2TimeByArea[ev][area];
            });
          });
        }
        if (!fieldTestPiCo2TimeByArea) {
          fieldTestPiCo2TimeByArea = moreFieldTestPiCo2TimeByArea;
        } else {
          Object.keys(moreFieldTestPiCo2TimeByArea).forEach((ev) => {
            Object.keys(moreFieldTestPiCo2TimeByArea[ev]).forEach((area) => {
              if (!fieldTestPiCo2TimeByArea[ev]) {
                fieldTestPiCo2TimeByArea[ev] = {};
              }
              if (!fieldTestPiCo2TimeByArea[ev][area]) {
                fieldTestPiCo2TimeByArea[ev][area] = 0;
              }
              fieldTestPiCo2TimeByArea[ev][area] += moreFieldTestPiCo2TimeByArea[ev][area];
            });
          });
        }

        const toBeContinued = Array.isArray(newContinueQueries) && newContinueQueries.length;

        this.state = {
          ...this.state,
          nblMinBtuHR: (!nblMinBtuHR || newNblMinBtuHR < nblMinBtuHR) ? newNblMinBtuHR : nblMinBtuHR,
          nblMaxBtuHR: (!nblMaxBtuHR || newNblMaxBtuHR > nblMaxBtuHR) ? newNblMaxBtuHR : nblMaxBtuHR,
          nblMinHrBPM: (!nblMinHrBPM || newNblMinHrBPM < nblMinHrBPM) ? newNblMinHrBPM : nblMinHrBPM,
          nblMaxHrBPM: (!nblMaxHrBPM || newNblMaxHrBPM > nblMaxHrBPM) ? newNblMaxHrBPM : nblMaxHrBPM,
          chapeaMinBtuHR: (!chapeaMinBtuHR || newChapeaMinBtuHR < chapeaMinBtuHR) ? newChapeaMinBtuHR : chapeaMinBtuHR,
          chapeaMaxBtuHR: (!chapeaMaxBtuHR || newChapeaMaxBtuHR > chapeaMaxBtuHR) ? newChapeaMaxBtuHR : chapeaMaxBtuHR,
          chapeaMinHrBPM: (!chapeaMinHrBPM || newChapeaMinHrBPM < chapeaMinHrBPM) ? newChapeaMinHrBPM : chapeaMinHrBPM,
          chapeaMaxHrBPM: (!chapeaMaxHrBPM || newChapeaMaxHrBPM > chapeaMaxHrBPM) ? newChapeaMaxHrBPM : chapeaMaxHrBPM,
          argosMinBtuHR: (!argosMinBtuHR || newArgosMinBtuHR < argosMinBtuHR) ? newArgosMinBtuHR : argosMinBtuHR,
          argosMaxBtuHR: (!argosMaxBtuHR || newArgosMaxBtuHR > argosMaxBtuHR) ? newArgosMaxBtuHR : argosMaxBtuHR,
          argosMinHrBPM: (!argosMinHrBPM || newArgosMinHrBPM < argosMinHrBPM) ? newArgosMinHrBPM : argosMinHrBPM,
          argosMaxHrBPM: (!argosMaxHrBPM || newArgosMaxHrBPM > argosMaxHrBPM) ? newArgosMaxHrBPM : argosMaxHrBPM,
          fieldTestMinBtuHR: (!fieldTestMinBtuHR || newFieldTestMinBtuHR < fieldTestMinBtuHR) ? newFieldTestMinBtuHR : fieldTestMinBtuHR,
          fieldTestMaxBtuHR: (!fieldTestMaxBtuHR || newFieldTestMaxBtuHR > fieldTestMaxBtuHR) ? newFieldTestMaxBtuHR : fieldTestMaxBtuHR,
          fieldTestMinHrBPM: (!fieldTestMinHrBPM || newFieldTestMinHrBPM < fieldTestMinHrBPM) ? newFieldTestMinHrBPM : fieldTestMinHrBPM,
          fieldTestMaxHrBPM: (!fieldTestMaxHrBPM || newFieldTestMaxHrBPM > fieldTestMaxHrBPM) ? newFieldTestMaxHrBPM : fieldTestMaxHrBPM,
          nblData: concatenateData(nblData, moreNblData, toBeContinued),
          chapeaData: concatenateData(chapeaData, moreChapeaData, toBeContinued),
          argosData: concatenateData(argosData, moreArgosData, toBeContinued),
          fieldTestData: concatenateData(fieldTestData, moreFieldTestData, toBeContinued),
          nblPiCo2TimeByArea,
          chapeaPiCo2TimeByArea,
          argosPiCo2TimeByArea,
          fieldTestPiCo2TimeByArea,
        };

        this.setState(this.state);
        if (toBeContinued) {
          this.resetState(null, newContinueQueries);
        }
      });
    };
  }

  connect(jwt, ghToken) {
    const url = `${config.wssUrl}?jwt=${jwt}`;
    const wsHelper = new WebsocketHelper();
    this.jwt = jwt;
    this.ghToken = ghToken;

    if (MOCK_ARGOS_HR) {
      // eslint-disable-next-line no-console
      console.log('🧪 WebSocketService: MOCK MODE — streaming synthetic ARGOS HR data');
      this.startMockArgosHr(wsHelper);
      this.state.connected = true;
      this.setState(this.state);
      return;
    }

    this.ws = new WebSocket(url);
    this.ws.onopen = () => {
      this.state.connected = true;
    };

    this.ws.onmessage = (event) => {
      const {
        hmdEventData,
        nblData,
        nblMaestroEventData,
        nblECSData,
        chapeaData,
        chapeaMaestroEventData,
        argosData,
        argosMaestroEventData,
        fieldTestData,
        fieldTestMaestroEventData,
      } = this.state;

      try {
        const e = JSON.parse(event.data);
        if (e.EventType && (e.EventType === 'ecs')) {
          wsHelper.handleECSEvent(e, nblECSData)
            .then((retNblECSData) => {
              this.state = {
                ...this.state,
                nblECSData: retNblECSData,
              };
              this.setState(this.state);
            });
        }
        if (e.EventType && (e.EventType === 'maestro-event')) {
          const { Location: location } = e || {};
          switch (determineMaestroEventLocation(location)) {
            case 'CHAPEA':
              wsHelper.handleMaestroEvent(e, chapeaMaestroEventData)
                .then((retMaestroEventData) => {
                  this.state = {
                    ...this.state,
                    chapeaMaestroEventData: retMaestroEventData,
                  };
                  this.setState(this.state);
                });
              break;
            case 'NBL':
              wsHelper.handleMaestroEvent(e, nblMaestroEventData)
                .then((retMaestroEventData) => {
                  this.state = {
                    ...this.state,
                    nblMaestroEventData: retMaestroEventData,
                  };
                  this.setState(this.state);
                });
              break;
            case 'ARGOS':
              wsHelper.handleMaestroEvent(e, argosMaestroEventData)
                .then((retMaestroEventData) => {
                  this.state = {
                    ...this.state,
                    argosMaestroEventData: retMaestroEventData,
                  };
                  this.setState(this.state);
                });
              break;
            case 'FIELDTEST':
              wsHelper.handleMaestroEvent(e, fieldTestMaestroEventData)
                .then((retMaestroEventData) => {
                  this.state = {
                    ...this.state,
                    fieldTestMaestroEventData: retMaestroEventData,
                  };
                  this.setState(this.state);
                });
              break;
            default:
          }
        }
        if (e.EventType && (e.EventType === 'hmd-event')) {
          wsHelper.handleHMDEvent(e, hmdEventData)
            .then((retHmdEventData) => {
              this.state = {
                ...this.state,
                hmdEventData: retHmdEventData,
              };
              this.setState(this.state);
            });
        } else {
          const { thing_name: thing } = e || {};
          switch (determineHHPModelEventLocation(thing)) {
            case 'CHAPEA':
              wsHelper.handleIncomingData(e, chapeaData, 'CHAPEA')
                .then(({
                  data,
                  minBtuHR,
                  maxBtuHR,
                  maxHrBPM,
                  minHrBPM,
                  piCo2TimeByArea,
                  suitPressure,
                }) => {
                  this.state = {
                    ...this.state,
                    chapeaData: data,
                    chapeaMinBtuHR: minBtuHR,
                    chapeaMaxBtuHR: maxBtuHR,
                    chapeaMinHrBPM: minHrBPM,
                    chapeaMaxHrBPM: maxHrBPM,
                    chapeaPiCo2TimeByArea: piCo2TimeByArea,
                    chapeaSuitPressure: suitPressure,
                  };
                  this.setState(this.state);
                });
              break;
            case 'NBL':
              wsHelper.handleIncomingData(e, nblData, 'NBL')
                .then(({
                  data,
                  minBtuHR,
                  maxBtuHR,
                  maxHrBPM,
                  minHrBPM,
                  piCo2TimeByArea,
                  suitPressure,
                }) => {
                  this.state = {
                    ...this.state,
                    nblData: data,
                    nblMinBtuHR: minBtuHR,
                    nblMaxBtuHR: maxBtuHR,
                    nblMinHrBPM: minHrBPM,
                    nblMaxHrBPM: maxHrBPM,
                    nblPiCo2TimeByArea: piCo2TimeByArea,
                    nblSuitPressure: suitPressure,
                  };
                  this.setState(this.state);
                });
              break;
            case 'ARGOS':
              wsHelper.handleIncomingData(e, argosData, 'ARGOS')
                .then(({
                  data,
                  minBtuHR,
                  maxBtuHR,
                  maxHrBPM,
                  minHrBPM,
                  piCo2TimeByArea,
                  suitPressure,
                }) => {
                  this.state = {
                    ...this.state,
                    argosData: data,
                    argosMinBtuHR: minBtuHR,
                    argosMaxBtuHR: maxBtuHR,
                    argosMinHrBPM: minHrBPM,
                    argosMaxHrBPM: maxHrBPM,
                    argosPiCo2TimeByArea: piCo2TimeByArea,
                    argosSuitPressure: suitPressure,
                  };
                  this.setState(this.state);
                });
              break;
            case 'FIELDTEST':
              wsHelper.handleIncomingData(e, fieldTestData, 'FIELDTEST')
                .then(({
                  data,
                  minBtuHR,
                  maxBtuHR,
                  maxHrBPM,
                  minHrBPM,
                  piCo2TimeByArea,
                  suitPressure,
                }) => {
                  this.state = {
                    ...this.state,
                    fieldTestData: data,
                    fieldTestMinBtuHR: minBtuHR,
                    fieldTestMaxBtuHR: maxBtuHR,
                    fieldTestMinHrBPM: minHrBPM,
                    fieldTestMaxHrBPM: maxHrBPM,
                    fieldTestPiCo2TimeByArea: piCo2TimeByArea,
                    fieldTestSuitPressure: suitPressure,
                  };
                  this.setState(this.state);
                });
              break;
            default:
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('unable to parse websocket event json');
      }
    };

    this.ws.onclose = () => {
      this.state.connected = false;
    };
  }

  // LOCAL MOCK ONLY — do not merge.
  async startMockArgosHr(wsHelper) {
    const buildEvent = (tick, ts, evNum) => {
      const base = evNum === 'EV1' ? 90 : 98;
      const amp = evNum === 'EV1' ? 25 : 30;
      const period = evNum === 'EV1' ? 10 : 13;
      const hrBpm = Math.round(
        base + Math.sin(tick / period) * amp + (Math.random() - 0.5) * 6,
      );
      return {
        thing_name: 'mock-argos',
        timestamp: ts,
        ev_num: evNum,
        hr_bpm: hrBpm,
        piCO2totals: {},
      };
    };

    const pushEvent = async (event) => {
      const { argosData } = this.state;
      const {
        data, minBtuHR, maxBtuHR, maxHrBPM, minHrBPM, piCo2TimeByArea,
      } = await wsHelper.handleIncomingData(event, argosData, 'ARGOS');
      this.state = {
        ...this.state,
        argosData: data,
        argosMinBtuHR: minBtuHR,
        argosMaxBtuHR: maxBtuHR,
        argosMinHrBPM: minHrBPM,
        argosMaxHrBPM: maxHrBPM,
        argosPiCo2TimeByArea: piCo2TimeByArea,
      };
      this.setState(this.state);
    };

    let tick = 0;
    const now = Date.now();
    for (let i = MOCK_SEED_POINTS; i > 0; i -= 1) {
      const ts = now - i * MOCK_TICK_MS;
      // eslint-disable-next-line no-await-in-loop
      await pushEvent(buildEvent(tick, ts, 'EV1'));
      // eslint-disable-next-line no-await-in-loop
      await pushEvent(buildEvent(tick, ts, 'EV2'));
      tick += 1;
    }

    this.mockArgosHrInterval = setInterval(async () => {
      const ts = Date.now();
      await pushEvent(buildEvent(tick, ts, 'EV1'));
      await pushEvent(buildEvent(tick, ts, 'EV2'));
      tick += 1;
    }, MOCK_TICK_MS);
  }

  close() {
    if (this.mockArgosHrInterval) {
      clearInterval(this.mockArgosHrInterval);
      this.mockArgosHrInterval = null;
    }
    if (this.ws) {
      this.ws.close();
    }
  }
}
